import { useUpdateEffect } from 'ahooks';
import Form from 'antd/es/form';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { DnsRecordFormFields, DnsRecordFormInputs, DnsRecordsQueryKeys, useCreateDnsRecordsMutation } from '@/modules/DnsRecords';
import { DomainsApiProviders } from '@/modules/DomainsApi';

import { DnsRecordsFormView } from './DnsRecordsFormView';
import { dnsRecordsFormValidationSchema } from './validation';

export const DnsRecordsCreateFormContainer: FC = memo(() => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<DnsRecordFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof DnsRecordFormInputs>>(null);
  const [isEnabledDomainsApi, setIsEnabledDomainsApi] = useState<boolean>(false);

  const apiProviderFormValue = Form.useWatch<DomainsApiProviders>(DnsRecordFormInputs.ApiProvider, form);

  const { mutate: createDnsRecordMutation, isLoading: createDnsRecordIsLoading } = useCreateDnsRecordsMutation()({
    onSuccess: async (data) => {
      appMessageApi.success('DNS запис успішно створено!');

      navigate(AppRoutes.DnsRecordsById.replace(':dnsRecordId', data.id));

      await queryClient.refetchQueries([DnsRecordsQueryKeys.GetAll]);
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof DnsRecordFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: DnsRecordFormFields = {
    name: undefined,
    apiProviderAccountId: undefined,
    apiProvider: undefined,
    records: undefined,
    record: undefined
  };

  const handleFinish = useCallback((values: DnsRecordFormFields): void => {
    setFormErrors(null);
    createDnsRecordMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.DnsRecords);
  }, []);

  // const handleGetDomainApiAccountsByProvider = useCallback((provider: DomainsApiProviders | null): void => {
  //   form.resetFields([DnsRecordFormInputs.ApiProviderAccountId]);

  //   if (provider) {
  //     if (provider === DomainsApiProviders.Cloudflare) autocompleteCAMutationResult.mutate();
  //   } else {
  //     if (provider === DomainsApiProviders.Cloudflare) autocompleteCAMutationResult.reset();
  //   }
  // }, []);

  const handleToggleDomainsApiForm = useCallback((value: boolean): void => {
    setIsEnabledDomainsApi(value);
    form.resetFields([DnsRecordFormInputs.ApiProvider, DnsRecordFormInputs.ApiProviderAccountId]);
  }, []);

  useUpdateEffect(() => {
    return () => {
      if (!apiProviderFormValue) form.setFieldValue(DnsRecordFormInputs.ApiProviderAccountId, undefined);
    };
  }, [apiProviderFormValue]);

  return (
    <DnsRecordsFormView
      formName="dns-record-create"
      formType="create"
      form={ form }
      formErrors={ formErrors }
      initialValues={ initialValues }
      isFormLoading={ createDnsRecordIsLoading }
      validationSchema={ dnsRecordsFormValidationSchema }
      isEnabledDomainsApi={ isEnabledDomainsApi }
      apiProviderFormValue={ apiProviderFormValue }
      handleToggleDomainsApiForm={ handleToggleDomainsApiForm }
      handleBack={ handleBack }
      handleFinish={ handleFinish }
      handleFinishFailed={ handleFinishFailed }
    />
  );
});
